import {ComponentType} from 'react';
import {
  CustomPartConversationMessage,
  MessagePartData,
} from '@stripe-internal/conversations-client';
import {
  MessagePartComponentProps,
  MessagePartComponentRegistry,
} from './MessagePartComponentContext';
import {devLog} from '../helpers/dev';

/**
 * The frontend twin of the backend part definition: one object per part
 * type, owned by the part's team, carrying everything a surface needs to
 * render it. Create with {@link defineMessagePartModule} and enable on a
 * surface with {@link composePartModules}.
 */
export type MessagePartModule<
  TPartType extends string = string,
  TContent extends {text: string} = {text: string},
> = {
  partType: TPartType;
  /** Rendering. `part.content` arrives typed as `TContent`; no casts. */
  Component: ComponentType<
    MessagePartComponentProps<
      CustomPartConversationMessage,
      MessagePartData<TContent>
    >
  >;
};

/**
 * A content-erased part module, storable in heterogeneous lists and
 * composable into a {@link MessagePartComponentRegistry}. Produce via
 * {@link defineMessagePartModule}.
 */
export type AnyMessagePartModule = {
  partType: string;
  Component: ComponentType<MessagePartComponentProps>;
};

export function defineMessagePartModule<
  TPartType extends string,
  TContent extends {text: string},
>(partModule: MessagePartModule<TPartType, TContent>): AnyMessagePartModule {
  // TypeScript won't allow a component typed for a specific TContent where
  // any content is possible, so we deliberately widen here, in one place.
  // This is type-level only: the backend Content schema, not this factory,
  // guarantees the content sent for a part type matches its component.
  return partModule as unknown as AnyMessagePartModule;
}

/**
 * Merges part modules into the registry shape consumed by
 * `MessagePartComponentProvider`. Later modules win on duplicate part types,
 * matching the provider's own spread semantics, so surfaces can override
 * platform defaults by listing their module last.
 */
export function composePartModules(modules: readonly AnyMessagePartModule[]): {
  components: MessagePartComponentRegistry;
  partTypes: string[];
} {
  const components: MessagePartComponentRegistry = {};
  for (const partModule of modules) {
    if (components[partModule.partType] !== undefined) {
      devLog('Duplicate message part module', {
        partType: partModule.partType,
        warning: 'Later module wins',
      });
    }
    components[partModule.partType] = partModule.Component;
  }
  return {components, partTypes: Object.keys(components)};
}
